import React, {useEffect, useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import styled from 'styled-components'
import {
  CustomUserProfileField,
  CustomUserProfileFields
} from '../../../common/types'
import {
  CUSTOM_FIELD_PATTERN,
  EDITABLE_PROFILE_FIELDS,
  RESERVED_FIELD_PREFIX
} from '../../../common/types/constants'
import {sanitizeRecord} from '../../../common/types/helpers'
import {BetaFlagMarker} from '../../../components/feature-flags'
import {InputWithHelp} from '../../../components/form'
import {Button} from '../../../components/library/buttons'
import {
  DropdownBody,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {Select, TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {List, NoResultsPermissions} from '../../../components/library/list'
import {FormModal} from '../../../components/modals'
import {
  useGetEnvironmentLazyQuery,
  useUpdateEnvironmentMutation
} from '../../../data/workbench-api/generated/environment'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {useToast} from '../../../lib/toast'
import {getCustomUserFields} from '../../../selectors/customers'
import {IconHeading, Section, SectionContent, SectionInfo} from '../components'

const FieldWrap = styled.div`
  margin-bottom: 10px;
`

export const FormError = styled.p`
  color: ${p => p.theme.red};
  align-items: center;
  justify-content: center;
  display: flex;
  text-align: center;
  min-height: 60px;
  padding: 20px;
  margin: 0 0 20px;
  border-radius: ${p => p.theme.borderRadius};
  background: ${p => p.theme.redLightest};
  line-height: ${p => p.theme.lineHeightCompact};
  font-size: ${p => p.theme.fontSize};
`

/** used to valid the custom field name input */
const validFieldName = (e: React.KeyboardEvent<HTMLDivElement>) => {
  // incantation based on library/text-editor.tsx which works
  const charStr = String.fromCharCode(e.charCode)
  // @ts-ignore - hmm this works, despite type warning, seems types are off here
  const attemptedValue = `${e.target.textContent}${charStr}`

  if (!CUSTOM_FIELD_PATTERN.test(attemptedValue)) {
    e.preventDefault()
  }
}

const FieldFormModal = ({
  field,
  isEdit,
  onSave,
  handleClose
}: {
  field: CustomUserProfileField
  isEdit: boolean
  onSave: (f: CustomUserProfileField) => Promise<void>
  handleClose: () => void
}) => {
  const [loading, setLoading] = useState(false)
  const [local, setLocal, dirty] =
    useStateChanged<CustomUserProfileField>(field)
  const [warning, setWarning] = useState(false)
  const wrappedHandleClose = () => {
    if (dirty) {
      if (confirm('You have unsaved changes are you sure?')) {
        handleClose()
      }
    } else {
      handleClose()
    }
  }
  const [nameFromLabel, setNameFromLabel] = useState(!local.name)
  // validation check
  const isValid =
    !loading &&
    !!(CUSTOM_FIELD_PATTERN.test(local.name) && local.label && local.type)

  return (
    <FormModal
      title={`Field ${local.name}`}
      handleClose={wrappedHandleClose}
      onSubmit={async () => {
        if (
          local.name.startsWith(RESERVED_FIELD_PREFIX) ||
          EDITABLE_PROFILE_FIELDS.includes(local.name)
        ) {
          setWarning(true)
          return
        }

        setLoading(true)
        await onSave(local)
        setLoading(false)
        handleClose()
      }}
      isValid={isValid}
    >
      {warning && (
        <FormError>Custom fields can not match profile fields.</FormError>
      )}
      <FieldWrap>
        <TextInput
          label="Field label"
          value={local.label || ''}
          onChange={label =>
            setLocal(f => {
              setWarning(false)

              return {
                ...(f ? f : {}),
                name: nameFromLabel
                  ? label.trim().replace(/\W+/g, '_').toLowerCase()
                  : f.name.toLowerCase(),
                label
              }
            })
          }
        />
      </FieldWrap>
      <FieldWrap>
        <InputWithHelp
          margin="0 0 0 10px"
          help={
            <>
              <p>
                Field names may only use alphanumeric or underscore characters.
              </p>
              <p>
                Existing user profile fields may not be used as custom field
                names.
              </p>
              <p>Field names cannot be edited after creation.</p>
            </>
          }
          renderInput={button => (
            <TextInput
              label="Field name"
              disabled={isEdit}
              value={local.name}
              buttonRight={button}
              onKeyPress={validFieldName}
              onChange={name => {
                setLocal(f => {
                  return {...f, name}
                })
                // Once the user has entered a name, stop auto-generating one
                setNameFromLabel(!name)
              }}
            />
          )}
        ></InputWithHelp>
      </FieldWrap>
      <FieldWrap>
        <Select<'date' | 'text'>
          options={['date', 'text']}
          value={local.type}
          onChange={type => {
            if (type)
              setLocal(f => {
                return {...f, type}
              })
          }}
          label="Field type"
        />
      </FieldWrap>
    </FormModal>
  )
}

type FieldRow = CustomUserProfileField & {id: string}

export const CustomFields = ({environment}: {environment: EnvironmentNode}) => {
  const toaster = useToast()
  const canView = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.View
  )
  const canEdit = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.Edit
  )
  const [query, environmentQuery] = useGetEnvironmentLazyQuery({
    variables: {id: environment.id}
  })

  useEffect(() => {
    if (canView) {
      query()
    }
  }, [query, canView])

  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed custom fields configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>customer-profiles>custom-fields'
      }
    })
  }, [analytics, environment])

  const preferences = environmentQuery?.data?.environment?.preferences

  const fields = useMemo(
    () =>
      getCustomUserFields(preferences).map(f => ({
        ...f,
        id: f.name
      })),
    [preferences]
  )
  const [doMutation, {loading, error}] = useUpdateEnvironmentMutation()
  const [modalField, setModalField] = useState<CustomUserProfileField>()

  useEffect(() => {
    if (error) {
      toaster(error.message, 'error')
    }
  }, [error, toaster])

  const doSave = async (customUserProfileFields: CustomUserProfileFields) => {
    if (!environment) {
      return
    }

    await doMutation({
      variables: {
        id: environment.id,
        preferences: {
          ...sanitizeRecord(preferences),
          customUserProfileFields
        }
      },
      onCompleted: () => {
        query() // is it possible to use the data that comes back instead of re-querying?
        analytics.track({
          event: 'Edited configuration',
          properties: {
            org_id: environment.organisationId,
            environment_id: environment.id,
            category: 'configuration>customer-profiles>custom-fields'
          }
        })
      }
    })
  }

  return (
    <Section>
      <Helmet>
        <title>Custom fields</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="input" /> Custom fields <BetaFlagMarker />
        </IconHeading>

        <nav>
          <Button disabled={!canEdit} onClick={() => setModalField({name: ''})}>
            New field
          </Button>
        </nav>
      </header>

      <SectionInfo>
        <p>
          Create custom fields that match your data. These fields will be
          available to view, edit, and filter.
        </p>
      </SectionInfo>

      <SectionContent>
        <List<FieldRow>
          bleed="#f2f2f2"
          loading={loading || environmentQuery.loading}
          columns={[
            {
              id: 'label',
              header: 'Label',
              accessor: f => f.label || f.name
            },
            {
              id: 'name',
              header: 'Name',
              accessor: f => f.name
            },
            {
              id: 'type',
              header: 'Type',
              accessor: f => f.type || 'text'
            }
          ]}
          rows={fields}
          renderOverflow={(field, closeDropdown) => (
            <Overflow
              field={field}
              onDeleteField={f =>
                doSave(fields.filter(({name}) => name !== f.name))
              }
              onEditField={f => setModalField(f)}
              closeDropdown={closeDropdown}
              canEdit={canEdit}
            />
          )}
          renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
        />
      </SectionContent>
      {modalField && (
        <FieldFormModal
          handleClose={() => setModalField(undefined)}
          field={modalField}
          isEdit={!!modalField.name}
          onSave={async field => {
            if (EDITABLE_PROFILE_FIELDS.includes(field.name)) {
              return
            }

            const updating = fields.some(f => f.name === field.name)
            const save = updating
              ? fields.map(f => (f.name === field.name ? field : f))
              : [...fields, field]

            await doSave(save)
          }}
        />
      )}
    </Section>
  )
}

const Overflow = ({
  field,
  onDeleteField,
  onEditField,
  closeDropdown,
  canEdit
}: {
  field: CustomUserProfileField
  onDeleteField: (f: CustomUserProfileField) => void
  onEditField: (f: CustomUserProfileField) => void
  closeDropdown: () => void
  canEdit: boolean
}) => {
  return (
    <DropdownBody>
      <DropdownItem disabled={!canEdit}>
        <a
          onClick={() => {
            closeDropdown()
            onEditField(field)
          }}
        >
          Edit field
        </a>
      </DropdownItem>
      <DropdownItem disabled={!canEdit}>
        <a
          onClick={() => {
            closeDropdown()
            onDeleteField(field)
          }}
        >
          Delete field
        </a>
      </DropdownItem>
    </DropdownBody>
  )
}
